import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import { useApi } from '@/components/Functional/hooks-core/useApi'
import { isValidCampaignSlug, useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { useLocale } from '@/components/Functional/hooks-core/useLocale'
import { useLogout } from '@/components/Functional/hooks/useLogout'
import type { MenuData } from '@/schemas/v2'
import { useTokenStore } from '@/store/useTokenStore'
import { Overlay, Portal, Transition } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { Fragment } from 'react'
import styles from './MyNaviMenuDrawer.module.css'

type MyNaviMenuDrawerProps = {
  isOpen: boolean
  onClose: () => void
  /** /api/v2/menus から取得した項目 */
  apiMenuItems?: MenuItem[]
  /** 「メールアドレスの登録/変更」押下時のコールバック */
  onEmailRegistration?: () => void
}

type MenuItem = Extract<MenuData, { type: 'URL' | 'STATIC' }>

export function MyNaviMenuDrawer({ isOpen, onClose, apiMenuItems, onEmailRegistration }: MyNaviMenuDrawerProps) {
  const { logout } = useLogout()
  const router = useRouter()
  const { campaignSlug } = useCampaignSlug()
  const { withLang } = useLocale()
  const idToken = useTokenStore((s) => s.idToken)
  const api = useApi()

  // メニュー下部に表示するユーザーID。認証必須のため未ログイン時は取得しない（＝非表示）。
  const { data: userProfile } = useQuery({
    queryKey: ['mynavi', 'userProfile', campaignSlug],
    queryFn: () => api.auth.fetchUserProfile(),
    enabled: !!idToken,
    staleTime: 5 * 60 * 1000,
  })

  const handleLogout = async () => {
    await logout()
    router.reload()
  }

  const staticActionUrl = (action: string): string | null => {
    const docsUrl = process.env.NEXT_PUBLIC_DOCS_URL
    // 利用規約・プライバシーはキャンペーンごとに内容が異なるため識別子付きパスを使う。
    // 識別子が無効（未取得・'[campaign]' 等）の場合は従来パスにフォールバックする。
    const docsBase = isValidCampaignSlug(campaignSlug) ? `${docsUrl}/${campaignSlug}` : docsUrl

    switch (action) {
      case 'CONTACT': {
        // mailto: にはロケールを付けない（#3504）
        const email = process.env.NEXT_PUBLIC_MYNAVI_CONTACT_EMAIL
        return email ? `mailto:${email}` : null
      }
      case 'FAQ':
        // バックエンド移行完了後に削除予定
        return withLang(`${docsUrl}/faq`)
      case 'TERMS':
        return withLang(`${docsBase}/terms`)
      case 'PRIVACY_POLICY':
        return withLang(`${docsBase}/privacy`)
      default:
        return null
    }
  }

  const itemClass = 'block leading-[1.5] text-left'

  const renderMenuItem = (item: MenuItem) => {
    switch (item.type) {
      case 'URL':
        if (!item.url || item.url === '#')
          return (
            <MyNaviTypography variant="points-label" as="span" className="block leading-[1.5] text-left">
              {item.name}
            </MyNaviTypography>
          )
        return (
          <a href={item.url} className={itemClass}>
            <MyNaviTypography variant="points-label" as="span">
              {item.name}
            </MyNaviTypography>
          </a>
        )
      case 'STATIC': {
        const url = staticActionUrl(item.action)
        if (url) {
          return (
            <a href={url} className={itemClass}>
              <MyNaviTypography variant="points-label" as="span">
                {item.name}
              </MyNaviTypography>
            </a>
          )
        }
        if (item.action === 'LOGOUT') {
          return (
            <button type="button" className={`${itemClass} text-white text-[14px]`} onClick={handleLogout}>
              {item.name || 'ログアウト'}
            </button>
          )
        }
        return null
      }
    }
  }

  // ログアウト項目はログイン時のみ表示 / メールアドレス項目は静的レンダリングするため除外
  // CONTACT はフロントエンドで独立して表示するため除外（重複防止）
  // CONTACT メールが設定されている場合は FAQ も非表示（CONTACT に置き換え済み）
  const displayItems = (apiMenuItems ?? []).filter((item) => {
    if (item.type === 'STATIC' && item.action === 'LOGOUT') {
      return !!idToken
    }
    if (item.type === 'STATIC' && item.action === 'CONTACT') {
      return false
    }
    // プライバシーポリシーは非表示
    if (item.type === 'STATIC' && item.action === 'PRIVACY_POLICY') {
      return false
    }
    if (item.type === 'STATIC' && item.action === 'FAQ') {
      return !process.env.NEXT_PUBLIC_MYNAVI_CONTACT_EMAIL
    }
    if (item.name.includes('メールアドレス')) {
      return false
    }
    return true
  })

  // Figma 準拠の並び順にするため種別ごとに分割する（フィルタ条件は変更しない）。
  // ① URL 項目（公式サイト等）を一番上に、⑤ ログアウトを一番下に固定する。
  const urlItems = displayItems.filter((item) => item.type === 'URL')
  const staticItems = displayItems.filter(
    (item): item is Extract<MenuItem, { type: 'STATIC' }> => item.type === 'STATIC' && item.action !== 'LOGOUT',
  )
  // displayItems の時点でログイン時のみ LOGOUT を含むため、ここでの login-gate は不要
  const logoutItem = displayItems.find(
    (item): item is Extract<MenuItem, { type: 'STATIC' }> => item.type === 'STATIC' && item.action === 'LOGOUT',
  )

  const renderContactItem = () => {
    const url = staticActionUrl('CONTACT')
    if (!url) return null
    return (
      <a href={url} className={itemClass}>
        <MyNaviTypography variant="points-label" as="span">
          お問い合わせ
        </MyNaviTypography>
      </a>
    )
  }

  const renderEmailRegistrationItem = () => {
    if (!idToken || !onEmailRegistration) return null
    return (
      <button
        type="button"
        className={`${itemClass} text-white text-[14px]`}
        onClick={() => {
          onClose()
          onEmailRegistration()
        }}
      >
        メールアドレスの登録/変更
      </button>
    )
  }

  return (
    <Portal>
      {/* 背景オーバーレイ */}
      <Transition mounted={isOpen} transition="fade" duration={200}>
        {(transitionStyles) => <Overlay onClick={onClose} zIndex={999} style={{ ...transitionStyles, top: 0 }} />}
      </Transition>

      {/* ドロワー本体 */}
      <Transition mounted={isOpen} transition="slide-left" duration={300} timingFunction="ease">
        {(transitionStyles) => (
          <div className={styles.drawer} style={transitionStyles}>
            {/* ✕ 閉じるボタン */}
            <button
              type="button"
              onClick={onClose}
              aria-label="メニューを閉じる"
              className="absolute top-[17px] right-[17px] z-10 text-white w-[24px] h-[24px] flex items-center justify-center"
            >
              <IconX size={24} stroke={1.5} style={{ minWidth: 24, minHeight: 24 }} />
            </button>

            {/* メニュー項目リスト（Figma 準拠の並び順） */}
            <div className={styles.contents}>
              {/* ① URL メニュー（公式サイト等）— 一番上 */}
              {urlItems.map((item, idx) => (
                <Fragment key={`mynavi-url-${item.url ?? idx}`}>{renderMenuItem(item)}</Fragment>
              ))}

              {/* ② メールアドレスの登録/変更（ログイン時のみ） */}
              {renderEmailRegistrationItem()}

              {/* ③ 固定メニュー（利用規約・よくある質問）— API 順を維持。プライバシーポリシーは非表示 */}
              {staticItems.map((item, idx) => (
                <Fragment key={`mynavi-static-${item.action ?? idx}`}>{renderMenuItem(item)}</Fragment>
              ))}

              {/* ④ お問い合わせ */}
              {renderContactItem()}

              {/* ⑤ ログアウト — 一番下（ログイン時のみ） */}
              {logoutItem && renderMenuItem(logoutItem)}
            </div>

            {/* ユーザーID — スクロール外のフッターとしてドロワー右下に固定表示（ログイン時のみ） */}
            {idToken && userProfile && (
              <MyNaviTypography variant="caption-white" as="p" className={`${styles.footer} leading-[1.5] opacity-80`}>
                id: {userProfile.user.id}
              </MyNaviTypography>
            )}
          </div>
        )}
      </Transition>
    </Portal>
  )
}
